import cx from 'classnames';
import React, { useState } from 'react';
import Modal from 'react-modal';
import MessageItemAttachment from '../../MessageItemAttachment';
import './Attachments.scss';
import { FileGQL } from '../../../../types/typescript/file';
import Audio from 'components/Audio';
import Video from 'components/Video';
import Image from 'components/Image';
import Preview from 'chat/components/_shared/Preview';
import MultiMultiImageFilePreviewModal from './MultiImageFilePreviewModal';
export interface Props {
  files?: FileGQL[];
  onSeeMore?: () => void;
  roundedTop?: boolean;
}

const Attachments = ({ files, onSeeMore, roundedTop }: Props) => {
  const filesCount = files?.length || 0;

  const [fileInFullScreenPreview, setFileInFullScreenPreview] = useState<FileGQL | undefined>(
    undefined
  );

  if (!filesCount) {
    return null;
  }

  let previewableFiles: FileGQL[] = [];
  let audioFiles: FileGQL[] = [];
  let nonPreviewableFiles: FileGQL[] = [];

  files?.forEach((file) => {
    if (Image.isSupportedType(file.contentType) || Video.isSupportedType(file.contentType)) {
      previewableFiles.push(file);
    } else if (Audio.isSupportedType(file.contentType)) {
      audioFiles.push(file);
    } else {
      nonPreviewableFiles.push(file);
    }
  });
  const previewableFilesCount = previewableFiles?.length || 0;
  const hasNonPreviableAttachments = nonPreviewableFiles.length > 0;
  const hasAudioAttachments = audioFiles.length > 0;
  const previewableContainerRoundedBottom = !hasNonPreviableAttachments;
  const audiosContainerRoundedBottom =
    hasAudioAttachments && !hasNonPreviableAttachments && !(previewableFilesCount > 0);

  const isSingleFile = previewableFiles && previewableFilesCount === 1;
  const isTwoFiles = previewableFiles && previewableFilesCount === 2;
  const isThreeFiles = previewableFiles && previewableFilesCount === 3;
  const isMoreThanThreeFiles = previewableFiles && previewableFilesCount > 3;

  const renderFullScreenPreviewOfFile = (file?: FileGQL) => {
    setFileInFullScreenPreview(file);
  };

  const internalOnSeeMore = () => {
    if (onSeeMore) {
      onSeeMore();
    } else {
      renderFullScreenPreviewOfFile(previewableFiles[2]);
    }
  };

  const renderMultiplePreviewableAttachments = () => {
    if (previewableFilesCount >= 2) {
      return (
        <>
          <div data-testid="multi-attachments-container" className="TwoOrMoreAttachmentContainer">
            <div
              data-testid="multi-attachments-container-first"
              className={cx('FirstAttachmentContainer', {
                rounded: previewableContainerRoundedBottom,
              })}
              key={previewableFiles[0].uuid}
            >
              <MessageItemAttachment
                file={previewableFiles[0]}
                onClick={() => renderFullScreenPreviewOfFile(previewableFiles[0])}
              />
            </div>
            <div
              data-testid="multi-attachments-container-second"
              className={cx('SecondAttachmentContainer', {
                rounded: isTwoFiles && previewableContainerRoundedBottom,
              })}
              key={previewableFiles[1].uuid}
            >
              <MessageItemAttachment
                file={previewableFiles[1]}
                onClick={() => renderFullScreenPreviewOfFile(previewableFiles[1])}
              />
            </div>
            {(isThreeFiles || isMoreThanThreeFiles) && (
              <div
                data-testid="multi-attachments-container-third"
                className={cx('ThirdAttachmentContainer', {
                  rounded: previewableContainerRoundedBottom,
                })}
                key={previewableFiles[2].uuid}
              >
                {isMoreThanThreeFiles && (
                  <div
                    data-testid="multi-attachments-see-more-overlay"
                    className="ThirdAttachmentContainerOverlay"
                  >
                    <div
                      className="ThirdAttachmentOverlay"
                      onClick={internalOnSeeMore}
                      data-testid="multi-attachments-see-more-text-container"
                    />
                    <span
                      data-testid="multi-attachments-see-more-text-span"
                      onClick={internalOnSeeMore}
                    >
                      +{previewableFilesCount - 2} More
                    </span>
                  </div>
                )}
                <MessageItemAttachment
                  file={previewableFiles[2]}
                  onClick={() => renderFullScreenPreviewOfFile(previewableFiles[2])}
                />
              </div>
            )}
          </div>
          <Modal isOpen={!!fileInFullScreenPreview}>
            <MultiMultiImageFilePreviewModal
              files={previewableFiles}
              selectedFile={fileInFullScreenPreview}
              onRequestHide={() => renderFullScreenPreviewOfFile(undefined)}
            />
          </Modal>
        </>
      );
    }
    return null;
  };

  const getNonPreviewableContent = (nonPreviewableFile: FileGQL) => {
    return {
      thumbnail: undefined,
      type: nonPreviewableFile.contentType,
      error: false,
      size: nonPreviewableFile.size,
      url: nonPreviewableFile.url,
      fileName: nonPreviewableFile.name,
      progress: 100,
    };
  };

  const renderMultipleAudioAttachments = () => {
    if (audioFiles.length > 0) {
      return (
        <div data-testid="audio-multi-attachment-container" className="AudioAttachmentContainer">
          {audioFiles.map((audio) => (
            <div
              className={cx('AudioAttachmentContainer-item', {
                rounded: audiosContainerRoundedBottom,
                square: !audiosContainerRoundedBottom,
              })}
              key={audio.uuid}
            >
              <MessageItemAttachment file={audio} />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderMultipleNonPreviewableAttachments = () => {
    if (hasNonPreviableAttachments) {
      return (
        <div
          data-testid="non-previewable-multi-attachment-container"
          className="NonPrevieawbleAttachmentContainer"
        >
          {nonPreviewableFiles.map((nonPreviewableFile) => (
            <div className="NonPrevieawbleAttachmentContainer-item" key={nonPreviewableFile.uuid}>
              <Preview
                filePreview={getNonPreviewableContent(nonPreviewableFile)}
                expand={true}
                downloadable={true}
                showTooltips={false}
              />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div
        data-testid="attachments-container"
        className={cx('Attachments', { roundedTop: roundedTop })}
      >
        {renderMultipleAudioAttachments()}
        {isSingleFile && (
          <div className={cx('OneAttachmentContainer', { previewableContainerRoundedBottom })}>
            <MessageItemAttachment file={previewableFiles[0]} />
          </div>
        )}
        {renderMultiplePreviewableAttachments()}
        {renderMultipleNonPreviewableAttachments()}
      </div>
    </>
  );
};

export default Attachments;
